import {api} from "@/shared/api";

import {BaseTimelinePage, TimelineConfig} from "./baseTimelinePage";
import {QuickSwitchProjectTimeEvent, QuickSwitchWorkingTimeEvent, WorkingTimeTypeFilter} from "@/features/timeline/timelineTypes";
import {
	EVENT_PROJECT_TIME_DELETED,
	EVENT_PROJECT_TIME_QUICK_SWITCH,
	EVENT_PROJECT_TIME_RESUMED,
	EVENT_WORKING_TIME_DELETED,
	EVENT_WORKING_TIME_QUICK_SWITCH,
	EVENT_WORKING_TIME_RESUME
} from "@/features/timeline/timelineConstants";
import {ProjectTimeDialog} from "@/features/projecttime/projectTimeDialog";
import {ProjectTimeStatistics} from "@/features/projecttime/projectTimeStatistics";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {WorkingTimeStatistics} from "@/features/workingtime/workingTimeStatistics";
import {processTaskDetailsUrlParams, TaskInfoDialog, TaskInfoDialogConfig} from "@/features/task/taskInfoDialog";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {CombinedRecordingStatusPanel} from "@/features/timeline/combinedRecordingStatusPanel";
import {StatusElementsHolder, statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {isProjectTimeIncluded, isWorkingTimeIncluded, RecordingType} from "@/features/timeline/recordingProcedure";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import {
	formatRunningDurationSince,
	getCurrentISOOffset,
	isSameDay,
	isSameOrBeforeDay,
	parseDateInput,
	parseISODate,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	CombinedRecordingStatus,
	ProjectTime,
	ProjectTimeStatus,
	RunningEntry,
	StatusElementEntries,
	Task,
	WorkingTimeStatus,
	WorkingTimeType
} from "@/shared/timr";
import {BreakTimeRecordingMode, RecordingMode, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {mapProjectTimeToReportProjectTime} from "@/shared/utils/recordMapper";

interface TimelinePersonalConfig extends TimelineConfig {
	workingTimeTypesForRecording: WorkingTimeType[];
	breakTimeTypesForRecording: WorkingTimeType[];
	combinedRecordingStatus: CombinedRecordingStatus;
	combinedRecordingEnabled: boolean;
	recordingMode: RecordingMode;
	lastProjectTime: ProjectTime | null;
}

/**
 * Personal timeline for the use of the timeline just for the current user.
 * Contains date filter input for a single day.
 * Filter input values are stored in session storage (independent of timelineReport)
 *
 */
export abstract class CombinedRecordingPage extends BaseTimelinePage {

	private workingTimeStatusRequest: JQueryXHR;
	private projectTimeStatusRequest: JQueryXHR;
	private combinedRecordingStatusPanel: CombinedRecordingStatusPanel;
	private readonly workingTimeStatistics: WorkingTimeStatistics;
	private readonly projectTimeStatistics: ProjectTimeStatistics;

	protected constructor(public config: TimelinePersonalConfig) {
		super(
			config,
			Temporal.Now.plainDateISO(),
			BrowserStorageSettings.getTimelineMode(BrowserStorageSettings.GROUP_TIMELINE_PERSONAL),
			BrowserStorageSettings.getTimelineSortDescending(BrowserStorageSettings.GROUP_TIMELINE_PERSONAL)
		);
		this.initDialogs();

		// load initial timeline with current user and today
		this.timelineComponent.load(globals.userId, Temporal.Now.plainDateISO());

		// init combined recording if both working and project time are enabled
		if (config.combinedRecordingEnabled) {
			if (isWorkingTimeIncluded(config.activeRecordingProcedure)) {
				this.workingTimeStatistics = new WorkingTimeStatistics($("#workingTimeStatistics"));
			} else if (isProjectTimeIncluded(config.activeRecordingProcedure)) {
				this.projectTimeStatistics = new ProjectTimeStatistics($("#projectTimeStatistics"));
			}

			this.initCombinedRecording();

			// process task details url params to show task info dialog
			processTaskDetailsUrlParams((taskId: number) => this.showTaskInfoDialog(taskId, globals.userId));
		}

		// refresh date selection and timeline if "today" button is selected but current date is outdated on window focus
		// e.g. "today" button is selected (10.6); without refresh a mismatch occurs because the next day (11.6) "today" button is still selected, but it is already the next day
		$(window)
			.focus(() => {
				if (this.isDateSelectionOutdated()) {
					this.updateDateFields(Temporal.Now.plainDateISO(), true);
				}
			});

		$(statusElementsHolder).on(StatusElementsHolder.EVENT_RECORDING_STATUS_CHANGED, (e, statusElementEntries: StatusElementEntries) => {
			this.updateRunningBadge(statusElementEntries);
		});
		this.updateRunningBadge(statusElementsHolder.getStatusElementEntries());
	}

	private updateRunningBadge(statusElementEntries: StatusElementEntries): void {
		const {runningWorkingTime} = statusElementEntries.recordingStatusEntries;
		const {runningProjectTime} = statusElementEntries.recordingStatusEntries;
		const workingTimeRunningDurationElement = document.getElementById("runningWorkingTimeDuration");
		const projectTimeRunningDurationElement = document.getElementById("runningProjectTimeDuration");

		if (workingTimeRunningDurationElement && runningWorkingTime) {
			workingTimeRunningDurationElement.innerHTML = formatRunningDurationSince(runningWorkingTime);
		}
		if (projectTimeRunningDurationElement && runningProjectTime) {
			projectTimeRunningDurationElement.innerHTML = formatRunningDurationSince(runningProjectTime);
		}
	}

	/**
	 * Initialize dialogs for editing working and project times in timeline report. User selection is not visible.
	 * @protected
	 */
	protected initDialogs(): void {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			customFieldDefinitions: this.config.customFieldDefinitionsWorkingTime,
			defaultStatusNumber: this.config.defaultStatusNumberWorkingTime,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.workingTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.workingTimeDateTimeChangeableByUserDayRangeMode,
			/* the following variables not necessary, the timeline is just for a single user and therefore the dialog also */
			userSelectionEnabled: false,
			allowedUserIds: [],
			users: [],
			isGroupLeaderForThemself: false
		});
		this.projectTimeDialog = new ProjectTimeDialog({
			dialogId: "#projectTimeDialog",
			customFieldDefinitions: this.config.customFieldDefinitionsProjectTime,
			defaultStatusNumber: this.config.defaultStatusNumberProjectTime,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.projectTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.projectTimeDateTimeChangeableByUserDayRangeMode,
			/* the following variables not necessary, the timeline is just for a single user and therefore the dialog also */
			userSelectionEnabled: false,
			allowedUserIds: [],
			users: [],
			isGroupLeaderForThemself: false
		});
		this.initDialogListeners();
	}

	/**
	 * Initialize dialog listeners.
	 * Includes listeners for working time statistics which are only relevant activated combined recording (only personal timeline)
	 * When the current work time is paused, the combined recording status window reloads whenever changes are made in the dialog that might affect the recording to be resumed, and displays updated information about the pause.
	 * Therefore, the combined recording status is always retrieved whenever a recording is modified, provided a pause is active.
	 */
	protected initDialogListeners(): void {
		super.initDialogListeners();

		// if no combined recording, no need to listen on further changes
		if (!this.config.combinedRecordingEnabled) {
			return;
		}

		$(this.workingTimeDialog)
			.on(RecordDialog.EVENT_RECORD_CREATED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateWorkingTimeStatistics();
			})
			.on(RecordDialog.EVENT_RECORD_EDITED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateWorkingTimeStatistics();
			})
			.on(RecordDialog.EVENT_RECORD_DELETED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateWorkingTimeStatistics();
			});

		$(this.projectTimeDialog)
			.on(RecordDialog.EVENT_RECORD_CREATED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateProjectTimeStatistics();
			})
			.on(RecordDialog.EVENT_RECORD_EDITED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateProjectTimeStatistics();
			})
			.on(RecordDialog.EVENT_RECORD_DELETED, () => {
				this.fetchCombinedRecordingStatusIfPaused();
				this.updateProjectTimeStatistics();
			});
	}

	/**
	 * Fetches the working time status from the server and updates the working time statistics.
	 */
	private updateWorkingTimeStatistics(): void {
		this.workingTimeStatusRequest?.abort();
		this.workingTimeStatusRequest = api("recording/getWorkingTimeStatus.json")
			.get({localTimeZone: getCurrentISOOffset()})
			.done((status: WorkingTimeStatus) => this.workingTimeStatistics.updateStatistics(status));
	}

	private updateProjectTimeStatistics(): void {
		if (this.projectTimeStatistics) {
			this.projectTimeStatusRequest?.abort();
			this.projectTimeStatusRequest = api("recording/getProjectTimeStatus.json")
				.get({localTimeZone: getCurrentISOOffset()})
				.done((status: ProjectTimeStatus) => this.projectTimeStatistics.updateStatistics(status));
		}
	}

	/**
	 * Reloads the timeline if there is a change in the running entries of the combined recording status
	 * and the affected record is within the currently visible timeline range. The reload is triggered only if necessary to avoid unnecessary operations
	 * When an entry is started or changed, the currently running recording is checked to see whether it is within the range
	 * While if stopped or deleted (no running recording available) the cached recording is checked
	 *
	 * @param {CombinedRecordingStatus} status - The current combined recording status containing
	 *        information about the running working time and project time.
	 * @return {void} - Returns nothing as this method performs an operation.
	 */
	protected reloadTimelineIfNecessary(status: CombinedRecordingStatus): void {
		const recordingHasChanged = (currentEntry?: RunningEntry, cachedEntry?: RunningEntry) => cachedEntry?.recordId !== currentEntry?.recordId || cachedEntry?.version !== currentEntry?.version;
		const parseStart = (entry?: RunningEntry) => parseISOZonedDateTime(entry?.start);

		// reload only if the running entry was changed and the recording (start) is inside the currently visible timeline range
		// if no current running entry is available e.g., after a stop or delete, the previously running recording (cached) is used to verify if it was inside the range and a reload is necessary
		const shouldReload = (currentEntry?: RunningEntry, cachedEntry?: RunningEntry, timelineDate?: Temporal.PlainDate | null) => {
			if (!recordingHasChanged(currentEntry, cachedEntry)) {
				return false;
			}

			// if no running entry is available (e.g. stop), use cached entry to determine if reload is necessary
			const recordStart = parseStart(currentEntry) ?? parseStart(cachedEntry);
			if (!recordStart) {
				return false;
			}

			// determine if the record start is inside the currently visible timeline range
			const recordStartDate = recordStart.toPlainDate();
			return this.isDailyModeSelected()
				? this.isSameDate(timelineDate, recordStartDate)
				: this.isWithinDateRange(timelineDate, recordStartDate);
		};

		const timelineDate = this.getTimelineDate();
		const {cachedCombinedRecordingStatus} = this.timelineComponent;

		if (shouldReload(status.workingTimeStatus?.runningWorkingTime, cachedCombinedRecordingStatus?.workingTimeStatus?.runningWorkingTime, timelineDate) ||
			shouldReload(status.projectTimeStatus?.runningProjectTime, cachedCombinedRecordingStatus?.projectTimeStatus?.runningProjectTime, timelineDate)) {
			this.timelineComponent.load(globals.userId, timelineDate);
		}
	}

	/**
	 * Triggers reload of timeline with selected user and date (start=end)
	 * If user is no group leader/multiuser user selection current user is used (user combobox is hidden)
	 */
	private reloadTimeline(): void {
		this.timelineComponent.load(globals.userId, this.getTimelineDate());
	}

	/**
	 * Opens add working time dialog (or add absence/vacation dialog) <p>
	 * For this purpose, the date fields are preselected by the provided timestamps.
	 * Using ${@link workingTimeTypeFilter} the wtt are filtered depending on which ones are required (absence or working time)
	 *
	 * @param workingTimeTypeFilter filter function to filter working time types, e.g. depending on whether add wt/absence
	 * @param start date time which should be preselected in the dialog
	 * @param end date time which should be preselected in the dialog
	 * @private
	 */
	protected onAddWorkingTime(workingTimeTypeFilter: WorkingTimeTypeFilter, start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		// filter working time types for the given dialog "add wt"/"add absence/vacation"
		const workingTimeTypes: WorkingTimeType[] = workingTimeTypeFilter(this.config.workingTimeTypes);

		// open working time dialog
		this.workingTimeDialog.create({workingTimeTypeId: workingTimeTypes[0]?.id}, false, workingTimeTypes); // createAnother=false to hide delete button
		if (start && end) {
			this.workingTimeDialog.setStartEndTime(start, end);
		}
	}

	protected onAddProjectTime(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		// preset project time dialog with values last project time of combined recording status, if not available use initial value of config
		const presetProjectTime = this.combinedRecordingStatusPanel?.combinedRecordingStatus.projectTimeStatus?.lastProjectTime ?? this.config.lastProjectTime;
		this.projectTimeDialog.create(mapProjectTimeToReportProjectTime(presetProjectTime));
		if (start && end) {
			this.projectTimeDialog.setStartEndTime(start, end);
		}
	}

	/**
	 * Determines the start/end date time preset for the top bar "add" button.
	 * In weekly mode the last-used entry's end is used. Daily mode keeps the selected day + now.
	 * Falls back to the default (selected timeline date + now) if no last recording is available.
	 */
	protected override getAddButtonPresetDateTime(type: RecordingType): {
		start: Temporal.ZonedDateTime,
		end: Temporal.ZonedDateTime
	} {
		const lastEnd = this.getLastRecordingEnd(type);
		if (lastEnd) {
			return {start: lastEnd, end: lastEnd.add({milliseconds: RecordDialog.DEFAULT_DURATION})};
		}
		return super.getAddButtonPresetDateTime(type);
	}

	/**
	 * Returns the end date/time of the last recorded entry for the given type from the combined recording status,
	 * or <code>null</code> if there is no last recording.
	 */
	private getLastRecordingEnd(type: RecordingType): Temporal.ZonedDateTime | null {
		const status = this.combinedRecordingStatusPanel?.combinedRecordingStatus ?? this.timelineComponent.combinedRecordingStatus;
		const lastEnd = type === "workingTime"
			? status?.workingTimeStatus?.lastWorkingTime?.end
			: status?.projectTimeStatus?.lastProjectTime?.end;
		return parseISOZonedDateTime(lastEnd);
	}

	/**
	 * Update sessionSettings variables and reloads timeline
	 */
	protected onInputChanged(): void {
		// update session storage variables for date and mode filter
		BrowserStorageSettings.setTimelineDate(BrowserStorageSettings.GROUP_TIMELINE_PERSONAL, parseDateInput(this.dateInput.value) ?? null);
		BrowserStorageSettings.setTimelineMode(BrowserStorageSettings.GROUP_TIMELINE_PERSONAL, this.modeControl.getValue());
		BrowserStorageSettings.setTimelineSortDescending(BrowserStorageSettings.GROUP_TIMELINE_PERSONAL, this.isSortDescending());

		this.updateTodayButtonState();
		this.reloadTimeline();
	}

	protected initEventListeners(): void {
		super.initEventListeners();

		// if no combined recording, no need to listen on further event changes (all related to combined recording status)
		if (!this.config.combinedRecordingEnabled) {
			return;
		}

		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_RESUME, () => {
			this.combinedRecordingStatusPanel.fetchRecordingStatus();
			this.reload();
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_RESUMED, () => {
			this.combinedRecordingStatusPanel.fetchRecordingStatus();
			this.reload();
		});

		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_QUICK_SWITCH, e => {
			const customEvent = e as CustomEvent<QuickSwitchWorkingTimeEvent>;
			this.combinedRecordingStatusPanel.quickSwitchCombinedRecording(customEvent.detail.quickSwitchProcedure, customEvent.detail.workingTime, null);
		});

		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_QUICK_SWITCH, e => {
			const customEvent = e as CustomEvent<QuickSwitchProjectTimeEvent>;
			this.combinedRecordingStatusPanel.quickSwitchCombinedRecording(customEvent.detail.quickSwitchProcedure, customEvent.detail.workingTime, customEvent.detail.projectTime);
		});


		// if a working/project time record is deleted, the combined recording status is fetched to check whether the deleted record was the currently running one, which would affect the combined recording status
		this.timelineComponent.addEventListener(EVENT_WORKING_TIME_DELETED, () => {
			this.fetchCombinedRecordingStatusIfPaused();
			if (this.workingTimeStatistics) {
				this.updateWorkingTimeStatistics();
			}
		});
		this.timelineComponent.addEventListener(EVENT_PROJECT_TIME_DELETED, () => {
			this.fetchCombinedRecordingStatusIfPaused();
			this.updateProjectTimeStatistics();
		});
	}

	protected initTaskInfoDialog(): void {
		const config: TaskInfoDialogConfig = {dialogId: "#taskInfoDialog"};

		// if combined recording is enabled and project time is active, the task info dialog should be able to switch or start a combined recording
		if (this.config.combinedRecordingEnabled && isProjectTimeIncluded(this.config.activeRecordingProcedure)) {
			config.onQuickSwitch = (task: Task) => this.combinedRecordingStatusPanel.switchOrStartWithTask(task);
			config.recordingStatus = this.config.combinedRecordingStatus;
		}

		this.taskInfoDialog = new TaskInfoDialog(config);
	}

	/**
	 * Initializes the combined recording status panel, which displays the current status of the combined recording and allows the user to interact with it (e.g., pause, resume, stop, quick switch). The panel is updated with the current combined recording status and reacts to changes in the recording status by updating the timeline and statistics accordingly.
	 */
	private initCombinedRecording(): void {
		this.combinedRecordingStatusPanel = new CombinedRecordingStatusPanel({
			panel: $("#combinedRecordingStatusPanel"),
			customFieldDefinitionsWorkingTime: this.config.customFieldDefinitionsWorkingTime,
			customFieldDefinitionsProjectTime: this.config.customFieldDefinitionsProjectTime,
			workingTimeTypes: this.config.workingTimeTypes,
			breakTimeTypes: this.config.breakTimeTypesForRecording,
			workingTimeTypesForRecording: this.config.workingTimeTypesForRecording,
			recordingMode: this.config.recordingMode,
			onUpdateTimeline: (status: CombinedRecordingStatus, forceReload?: boolean) => forceReload ? this.reloadTimeline() : this.reloadTimelineIfNecessary(status),
			onStatusUpdate: (status: CombinedRecordingStatus) => {
				if (isWorkingTimeIncluded(this.config.activeRecordingProcedure) && status.workingTimeStatus) {
					this.workingTimeStatistics?.updateStatistics(status.workingTimeStatus);
				} else if (isProjectTimeIncluded(this.config.activeRecordingProcedure) && status.projectTimeStatus) {
					this.projectTimeStatistics?.updateStatistics(status.projectTimeStatus);
				}

				// update the combined recording status in the timeline component (used for - context menu - actions)
				this.timelineComponent.combinedRecordingStatus = status;

				// update the combined recording status in the task info dialog to show the correct buttons in it
				this.taskInfoDialog?.updateRecordingStatus(status);
			},
			activeRecordingProcedure: this.config.activeRecordingProcedure
		});
		this.combinedRecordingStatusPanel.updateStatus(this.config.combinedRecordingStatus, this.config.activeRecordingProcedure);
	}

	// if a pause is currently in progress, the combined recording panel should be reloaded (via status) to display updated information about the pause, which will continue after resume
	private fetchCombinedRecordingStatusIfPaused(): void {
		if (globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION &&
			this.combinedRecordingStatusPanel?.combinedRecordingStatus?.workingTimeStatus?.runningWorkingTime?.workingTimeType.category === WorkingTimeTypeCategory.BreakTime) {
			this.combinedRecordingStatusPanel.fetchRecordingStatus();
		}
	};

	/**
	 * Determines if the currently selected date is outdated by checking whether the "today" button is selected (disabled state) and the selected timeline date does not match today's date.
	 * E.g. "today" button is selected (10.6). Without refresh a mismatch occurs because the next day (11.6) "today" button is still selected, but it is already the next day
	 *
	 * @return {boolean} True if the date selection is outdated, false otherwise.
	 */
	private isDateSelectionOutdated(): boolean {
		const today = Temporal.Now.plainDateISO();
		return this.todayButton.disabled && (this.isDailyModeSelected() && !this.isSameDate(this.getTimelineDate(), today) ||
			!this.isDailyModeSelected() && !this.isWithinDateRange(this.getTimelineDate(), today));
	}

	private isWithinDateRange(timelineDate: Temporal.PlainDate | null, date: Temporal.PlainDate): boolean {
		return isSameOrBeforeDay(timelineDate, date) && isSameOrBeforeDay(date, parseDateInput(this.dateToInput.value));
	}

	private isSameDate(timelineDate: Temporal.PlainDate | null, date: Temporal.PlainDate): boolean {
		return isSameDay(timelineDate, date);
	}
}
